import { useEffect, useState } from 'react';
import { Bot, User, MessageCircle, Sparkles, Zap, Brain } from 'lucide-react';

interface Message {
  id: number;
  type: 'ai' | 'human';
  x: number;
  y: number;
  delay: number;
}

interface Connection {
  id: number;
  from: { x: number; y: number };
  to: { x: number; y: number };
  delay: number;
}

export function ContactBackground() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number; size: number; delay: number }[]>([]);

  useEffect(() => {
    // Generate AI and Human nodes
    const newMessages: Message[] = [];
    const aiPositions = [
      { x: 8, y: 45 },
      { x: 5, y: 70 },
      { x: 12, y: 90 },
    ];
    const humanPositions = [
      { x: 92, y: 40 },
      { x: 95, y: 65 },
      { x: 88, y: 85 },
    ];

    aiPositions.forEach((pos, i) => {
      newMessages.push({ id: i, type: 'ai', x: pos.x, y: pos.y, delay: i * 0.5 });
    });

    humanPositions.forEach((pos, i) => {
      newMessages.push({ id: i + 3, type: 'human', x: pos.x, y: pos.y, delay: i * 0.5 + 0.3 });
    });

    setMessages(newMessages);

    // Generate connections between AI and Humans
    const newConnections: Connection[] = [
      { id: 0, from: { x: 8, y: 45 }, to: { x: 92, y: 40 }, delay: 0 },
      { id: 1, from: { x: 5, y: 70 }, to: { x: 95, y: 65 }, delay: 0.8 },
      { id: 2, from: { x: 12, y: 90 }, to: { x: 88, y: 85 }, delay: 1.6 },
      { id: 3, from: { x: 8, y: 45 }, to: { x: 95, y: 65 }, delay: 2.4 },
      { id: 4, from: { x: 5, y: 70 }, to: { x: 88, y: 85 }, delay: 3.2 },
    ];
    setConnections(newConnections);

    // Generate floating particles
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-purple-500/5" />

      {/* Animated glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      {/* Connection lines with animated pulse */}
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgb(139, 92, 246)" stopOpacity="0.6" />
            <stop offset="50%" stopColor="rgb(168, 85, 247)" stopOpacity="0.8" />
            <stop offset="100%" stopColor="rgb(139, 92, 246)" stopOpacity="0.6" />
          </linearGradient>
        </defs>
        {connections.map((conn) => (
          <g key={conn.id}>
            {/* Base line */}
            <line
              x1={`${conn.from.x}%`}
              y1={`${conn.from.y}%`}
              x2={`${conn.to.x}%`}
              y2={`${conn.to.y}%`}
              stroke="url(#connectionGradient)"
              strokeWidth="1"
              strokeDasharray="8 4"
              opacity="0.3"
            />
            {/* Animated pulse on line */}
            <circle r="4" fill="rgb(139, 92, 246)" opacity="0.8">
              <animateMotion
                dur="3s"
                repeatCount="indefinite"
                begin={`${conn.delay}s`}
                path={`M${conn.from.x * 10} ${conn.from.y * 3} L${conn.to.x * 10} ${conn.to.y * 3}`}
              />
            </circle>
          </g>
        ))}
      </svg>

      {/* AI Nodes */}
      {messages.filter(m => m.type === 'ai').map((msg) => (
        <div
          key={msg.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${msg.x}%`,
            top: `${msg.y}%`,
            animation: `fadeInScale 0.6s ease-out ${msg.delay}s both, float 4s ease-in-out infinite ${msg.delay}s`,
          }}
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 w-16 h-16 bg-accent/30 rounded-2xl blur-xl animate-pulse" />
            {/* Icon container */}
            <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-purple-600 flex items-center justify-center shadow-xl shadow-accent/25">
              {msg.id === 0 && <Bot className="text-white" size={28} />}
              {msg.id === 1 && <Brain className="text-white" size={28} />}
              {msg.id === 2 && <Sparkles className="text-white" size={28} />}
            </div>
            {/* Label */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-accent font-medium whitespace-nowrap">
              AI Agent
            </div>
          </div>
        </div>
      ))}

      {/* Human Nodes */}
      {messages.filter(m => m.type === 'human').map((msg) => (
        <div
          key={msg.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: `${msg.x}%`,
            top: `${msg.y}%`,
            animation: `fadeInScale 0.6s ease-out ${msg.delay}s both, float 4s ease-in-out infinite ${msg.delay + 0.5}s`,
          }}
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 w-14 h-14 bg-emerald-500/30 rounded-xl blur-xl animate-pulse" />
            {/* Icon container */}
            <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl shadow-emerald-500/25">
              <User className="text-white" size={24} />
            </div>
            {/* Label */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-emerald-400 font-medium whitespace-nowrap">
              Developer
            </div>
          </div>
        </div>
      ))}

      {/* Floating message bubbles */}
      <div
        className="absolute hidden lg:block"
        style={{
          left: '30%',
          top: '70%',
          animation: 'floatMessage 6s ease-in-out infinite',
        }}
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 border border-accent/30 backdrop-blur-sm">
          <MessageCircle size={14} className="text-accent" />
          <span className="text-xs text-accent">Mesaj gönderildi</span>
        </div>
      </div>

      <div
        className="absolute hidden lg:block"
        style={{
          left: '65%',
          top: '75%',
          animation: 'floatMessage 6s ease-in-out infinite 2s',
        }}
      >
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 backdrop-blur-sm">
          <Zap size={14} className="text-purple-400" />
          <span className="text-xs text-purple-400">7/24 Destek</span>
        </div>
      </div>

      {/* Floating particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-accent/40"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            animation: `floatParticle 8s ease-in-out infinite ${particle.delay}s`,
          }}
        />
      ))}

      {/* Animation keyframes */}
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.5);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translate(-50%, -50%) translateY(0px);
          }
          50% {
            transform: translate(-50%, -50%) translateY(-10px);
          }
        }

        @keyframes floatMessage {
          0%, 100% {
            opacity: 0.7;
            transform: translateY(0px) scale(1);
          }
          25% {
            opacity: 1;
            transform: translateY(-5px) scale(1.02);
          }
          50% {
            opacity: 0.8;
            transform: translateY(5px) scale(0.98);
          }
          75% {
            opacity: 1;
            transform: translateY(-3px) scale(1.01);
          }
        }

        @keyframes floatParticle {
          0%, 100% {
            transform: translateY(0px) translateX(0px);
            opacity: 0.4;
          }
          25% {
            transform: translateY(-20px) translateX(10px);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-10px) translateX(-5px);
            opacity: 0.5;
          }
          75% {
            transform: translateY(-25px) translateX(5px);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
}
